import React, { createContext, useContext, useEffect, useMemo } from 'react'
import { useRouter } from 'next/router'
import { UserProvider, useUser } from '@auth0/nextjs-auth0/client'

import {
  AuthPathKey,
  AuthPathOptions,
  createAuthPathname,
  getMembershipIdFromSub,
} from '@reward-platform/auth0-b2c'
import SpinnerOverlay from '~/components/shared/SpinnerOverlay'
import { PlatformError } from '~/utils/errors'
import { isClient } from '~/utils/envChecks'

import { usePartner } from '../partner'
import { useLocalisation } from '../localisation'
import { AuthContextValue, AuthProviderProps } from './Auth.types'

const AuthContext = createContext<AuthContextValue | undefined>(undefined)

const handleClientAuthRoute = (type: AuthPathKey, options: AuthPathOptions) => {
  if (isClient()) {
    const pathname = `${createAuthPathname(type, { ...options })}`
    window.location.assign(pathname)
  }
}

const AuthContextProvider = ({ children }: React.PropsWithChildren) => {
  const authUser = useUser()
  const { basePath } = usePartner()
  const { locale } = useLocalisation()
  const { user } = authUser
  const userId = user?.sub ? getMembershipIdFromSub(user.sub) : null
  const isAuthenticated = !!userId

  useEffect(() => {
    sessionStorage.setItem('authenticated_user', isAuthenticated ? 'true' : 'false')
  }, [isAuthenticated])

  const contextValue = useMemo<AuthContextValue>(() => {
    return {
      ...authUser,
      userId,
      isAuthenticated,
      handleLogin: (options) =>
        handleClientAuthRoute('LOGIN', { basePath, ...options, returnToLocale: locale }),
      handleLogout: (options) => handleClientAuthRoute('LOGOUT', { basePath, ...options }),
      getAuthHref: (type, options) => createAuthPathname(type, { basePath, ...options }),
    }
  }, [authUser, userId, isAuthenticated, basePath, locale])

  return <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>
}

export const AuthProvider = ({
  isLoading,
  ...rest
}: React.PropsWithChildren<AuthProviderProps>) => {
  const router = useRouter()
  const { basePath } = usePartner()

  if (isLoading) {
    return <SpinnerOverlay />
  }

  return (
    <UserProvider
      loginUrl={createAuthPathname('LOGIN', { basePath, returnToPath: router.asPath })}
      profileUrl={createAuthPathname('PROFILE', { basePath })}
    >
      <AuthContextProvider {...rest} />
    </UserProvider>
  )
}

export const useAuthContext = (): AuthContextValue => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new PlatformError('useAuthContext must be used within an AuthProvider')
  }
  return context ?? {}
}

export default AuthContext
